import { useState, useEffect, useCallback } from "react";
import { C, Card, Badge, Banner, Loading, EmptyState } from "./components/ui.jsx";
import SummaryCards from "./components/SummaryCards.jsx";
import QueueTable from "./components/QueueTable.jsx";
import ControlSummary from "./components/ControlSummary.jsx";
import ComparePanel from "./components/ComparePanel.jsx";
import { createBirthdayApi, cairoToday } from "./api.js";

const isOn = (v) => v === true || v === "true";

/* Read-only Birthday Automation dashboard. No action buttons (approve/reject/
 * send/force-send) — surfacing only. Visible to admin/hr; RLS protects data. */
export default function BirthdayAutomationTab({ anonKey, session, userRole }) {
  const token = session?.access_token;
  const allowed = userRole === "admin" || userRole === "hr";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // { tone, text } — action feedback
  const [queue, setQueue] = useState({ date: cairoToday().iso, rows: [] });
  const [settings, setSettings] = useState({});
  const [routing, setRouting] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dryRunJobs, setDryRunJobs] = useState(0);

  const api = createBirthdayApi(anonKey, token);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [q, s, r, t, j] = await Promise.all([
        api.getTodayQueue(), api.getSettings(), api.getRoutingRules(),
        api.getTemplates(), api.getDryRunJobCount(),
      ]);
      setQueue(q); setSettings(s); setRouting(r); setTemplates(t); setDryRunJobs(j);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonKey, token]);

  /* Lightweight refresh after an action — updates the queue + summary WITHOUT
   * flipping the full-page loading state (so the expanded row stays put). */
  const refreshQueue = useCallback(async () => {
    try {
      const [q, j] = await Promise.all([api.getTodayQueue(), api.getDryRunJobCount()]);
      setQueue(q); setDryRunJobs(j);
    } catch (e) {
      setNotice({ tone: "danger", text: `Could not refresh the queue: ${e.message}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonKey, token]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  if (!allowed) {
    return (
      <div>
        <Card><EmptyState title="Access restricted" sub="Birthday Automation is available to Admin and HR roles only." /></Card>
      </div>
    );
  }

  const automationOff = !isOn(settings["birthday.automation_enabled"]);
  const dryRun = isOn(settings["birthday.dry_run_enabled"]);
  const n8nOff = !isOn(settings["birthday.n8n_integration_enabled"]);

  return (
    <div>
      {/* Header — matches the standard Nexus tab header (Offer Creation / Admin Settings) */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 4px", letterSpacing: "-.5px" }}>Birthday Automation</h2>
          <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>
            Today's queue and controls (Africa/Cairo) · {queue.date} · read-only
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {dryRun && <Badge label="Dry-run active" bg="#E3F2FD" color="#1565C0" />}
          {n8nOff && <Badge label="n8n disabled" bg="#F4F2EE" color={C.sub} />}
          {automationOff && <Badge label="Automation disabled" bg="#FFF8E1" color="#B7791F" />}
        </div>
      </div>

      {notice && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone={notice.tone}>
            <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <span>{notice.text}</span>
              <button onClick={() => setNotice(null)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "inherit", lineHeight: 1, opacity: .7 }}
                aria-label="Dismiss">×</button>
            </span>
          </Banner>
        </div>
      )}

      {loading ? (
        <Loading label="Loading birthday data…" />
      ) : error ? (
        <Banner tone="danger">Could not load birthday data: {error}</Banner>
      ) : (
        <>
          <Banner tone="neutral">
            This is a read-only view. No approve/reject/send actions are available here. Sending is disabled
            (dry-run on, n8n integration off); nothing on this page can send an email or call n8n.
          </Banner>

          <div style={{ height: 16 }} />
          <SummaryCards rows={queue.rows} dryRunJobs={dryRunJobs} />

          <Card style={{ padding: 0 }}>
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, fontSize: 14, fontWeight: 600 }}>
              Today's Queue
            </div>
            <div style={{ padding: queue.rows.length ? 0 : 8 }}>
              <QueueTable
                rows={queue.rows}
                api={api}
                allowed={allowed}
                reload={refreshQueue}
                onNotice={(tone, text) => setNotice({ tone, text })}
              />
            </div>
          </Card>

          <ControlSummary settings={settings} routing={routing} templates={templates} />

          <ComparePanel api={api} defaultDate={queue.date} />
        </>
      )}
    </div>
  );
}
